import '../player/webdriver'; // preload
import * as config from '../commons/config';
import * as perf from '../commons/performance-logger';
import * as analyticsService from '../services/analyticsService';
import * as testimCustomToken from '../commons/testimCustomToken';
import { labFeaturesService } from '../services/labFeaturesService';
import { ArgError, StopRunOnError } from '../errors';
import { getLogger } from '../commons/logger';
import { CLI_MODE } from '../commons/constants';
import { ExecutionQueue } from '../executionQueue';
import { extensionBrowsers, seleniumBrowsers } from './runnerUtils';
import type { BrowserType, RunnerMode } from './runnerUtils';
import type { RunnerOptions } from '../runOptions';
import type { ExecutionList } from './TestPlanRunner';
import type { TestRunStatus } from '../testRunStatus';
import type { SerializedGrid } from '../services/gridService';
import type { TestResultWithStats } from '../workers/BaseWorker';
import type { PartialLoginData } from 'clickim/src/common/authenticationService';

const logger = getLogger('worker-manager');

type RemoveFirst<T extends unknown[]> = T extends [unknown, ...infer R] ? R : T;
export type BaseWorkerArgs = RemoveFirst<ConstructorParameters<typeof import('../workers/BaseWorker').BaseWorker>>;
export interface TestResultUpdates {
    show?: true;
    seleniumStats?: any;
    gridIssues?: any;
    keepAliveIssue?: any;
    gridHost?: string;
    gridName?: string;
    gridType?: string;
    gridProvider?: string;
}

export interface IgnoredTestResult {
    name?: string;
    testId: string;
    resultId: string;
    runnerStatus: 'SKIPPED';
    testStatus?: string;
    mobile?: { [key in 'isAppFromDevice' | 'isAppForIosVirtualDevice']?: boolean };
}

/*
    * WorkerManager is responsible for running tests and to maintain parallel based on user input.
    * It creates workers based on the browser type, or based on runner mode (if specified).
    * It also manages the number of parallel workers running at a time, to not exceed the user specified parallel count.
    * The magic occurs when runTests is being called, then runAndWaitToComplete is a promise that resolves when all tests are done using onTestCompleted callback.
    * The onTestCompleted callback is called when a test is done, and it updates the test status and the combinedTestResults object, which eventually resolves the promise.
*/
export class WorkerManager {
    private workerIdCount = 0;
    private parallelCount = 0;
    private workers = new Map();
    private executionQueue!: ExecutionQueue;
    private combinedTestResults: { [testResultId: string]: any } = {};
    private workerUniqueIds: string[] = [];
    constructor(private readonly customExtensionLocalLocation?: string) { }

    // Public for UT purposes.
    public async getWorkerType({ mode, localTmaUrl }: { mode?: RunnerMode; localTmaUrl?: string }, browser: BrowserType) {
        if (mode) {
            switch (mode) {
                case CLI_MODE.SELENIUM:
                    return (await import('../workers/WorkerSelenium')).WorkerSelenium;
                case CLI_MODE.APPIUM:
                    if (localTmaUrl) {
                        return (await import('../workers/WorkerLocalTma')).WorkerLocalTma;
                    }
                    return (await import('../workers/WorkerAppium')).WorkerAppium;
                default:
                    if (labFeaturesService.isFeatureAvailableForProject('useSameBrowserForMultiTests')) {
                        return (await import('../workers/WorkerExtensionSingleBrowser')).WorkerExtensionSingleBrowser;
                    }
                    return (await import('../workers/WorkerExtension')).WorkerExtension;
            }
        } else { // The user didn't specify a mode, select worker based on browser type.
            switch (browser) {
                case extensionBrowsers.find(b => b === browser) && labFeaturesService.isFeatureAvailableForProject('useSameBrowserForMultiTests'):
                    return (await import('../workers/WorkerExtensionSingleBrowser')).WorkerExtensionSingleBrowser;
                case extensionBrowsers.find(b => b === browser):
                    return (await import('../workers/WorkerExtension')).WorkerExtension;
                case seleniumBrowsers.find(b => b === browser):
                    return (await import('../workers/WorkerSelenium')).WorkerSelenium;
                default:
                    throw new ArgError(`browser ${browser} is not supported`);
            }
        }
    }

    private async createWorker(
        browser: BrowserType,
        ...args: BaseWorkerArgs
    ) {
        const runnerOptions = args[0];
        const Worker = await this.getWorkerType({ mode: runnerOptions.mode, localTmaUrl: runnerOptions.localTmaUrl }, browser);
        try {
            perf.log('before new Worker', runnerOptions.mode);
            return new Worker(this.executionQueue, ...args);
        } finally {
            perf.log('after new Worker', runnerOptions.mode);
        }
    }

    public async runTests(
        testList: ExecutionList,
        testStatus: TestRunStatus,
        executionId: string,
        executionName: string,
        options: RunnerOptions,
        branchToUse: string,
        workerCount: number,
        stopOnError: boolean,
    ) {
        if (testList && testList.length === 0) {
            return undefined;
        }
        let stoppedOnError = false;
        let runningTests = 0;
        const runAndWaitToComplete = (token: string, userAccessKey?: string) => new Promise<{ [testResultId: string]: any }>((resolve) => {
            const projectId = options.project;
            const testCount = testList.length;
            this.parallelCount = workerCount;
            this.workerUniqueIds = this.generateUniqueIds(this.parallelCount);

            this.executionQueue = new ExecutionQueue(executionId, executionName, testList, options, branchToUse, testStatus);
            this.combinedTestResults = {};

            const ucid = options.company?.ucid || '';
            const companyId = options.company?.companyId;
            const companyName = options.company?.name;
            const source = options.source || 'cli';
            const user = options.user;
            const companyPlan = options.company?.planType;
            const isStartUp = options.company?.isStartUp;
            const projectName = options.projectData?.name;
            const projectType = options.projectData?.type;
            const lightweightMode = options.lightweightMode;
            const gridType = options.gridData?.type;

            const onTestStarted = (wid: number, testId: string, resultId: string, isRerun: boolean | undefined, testRetryKey: `${number}:${number}`, appSource?: string) => {
                runningTests++;
                analyticsService.analyticsTestStart({
                    executionId,
                    projectId,
                    testId,
                    resultId,
                    ucid,
                    companyId,
                    companyName,
                    projectName,
                    companyPlan,
                    source,
                    user,
                    lightweightMode,
                    isStartUp,
                    projectType,
                    appSource,
                    gridType,
                });
                return testStatus.testStartAndReport(wid, executionId, resultId, isRerun, testRetryKey);
            };

            const onTestCompleted = async (wid: number, testId: string, testResult: TestResultWithStats, sessionId: string, isRerun: boolean | undefined, appSource?: string) => {
                runningTests--;
                const update: TestResultUpdates = {
                    ...(lightweightMode?.onlyTestIdsNoSuite && { show: true }),
                    ...(testResult.seleniumStats && { seleniumStats: testResult.seleniumStats }),
                    ...(testResult.gridIssues && { gridIssues: testResult.gridIssues }),
                    ...(testResult.keepAliveIssue && { keepAliveIssue: testResult.keepAliveIssue }),
                    ...(options.host && { gridHost: options.host }),
                };
                if (testResult.seleniumPerfMarks) {
                    testStatus.concatSeleniumPerfMarks(testResult.seleniumPerfMarks);
                    // @ts-expect-error it's probably not a good idea to combine the testResult with the stats just to remove them later
                    delete testResult.seleniumPerfMarks;
                }
                if (options.grid || options.gridId) {
                    update.gridName = options.grid || options.gridData?.name;
                    update.gridType = options.gridData?.type;
                    update.gridProvider = options.gridData?.provider;
                } else if (options.localTmaUrl) {
                    update.gridName = 'local-tma-from-options';
                    update.gridType = 'local-tma';
                } else if (options.useLocalChromeDriver) {
                    update.gridName = 'local-chrome-driver-from-options';
                    update.gridType = 'local-chrome';
                } else if (options.useChromeLauncher) {
                    update.gridName = 'chrome-launcher-from-options';
                    update.gridType = 'local-chrome';
                } else if (options.browserstack) {
                    update.gridName = 'browserstack-from-options';
                } else if (options.saucelabs) {
                    update.gridName = 'saucelabs-from-options';
                }

                await testStatus.testEndAndReport(wid, testResult, executionId, sessionId, isRerun, update)
                    .catch(err => logger.error('testEndAndReport threw an error', { err }));

                if (isRerun) {
                    return;
                }
                this.combinedTestResults[testResult.resultId] = testResult;
                analyticsService.analyticsTestEnd({
                    executionId,
                    projectId,
                    testId,
                    resultId: testResult.resultId,
                    result: testResult,
                    ucid,
                    companyId,
                    companyName,
                    projectName,
                    companyPlan,
                    source,
                    user,
                    lightweightMode,
                    logger,
                    isStartUp,
                    projectType,
                    appSource,
                });
                if (stopOnError && !testResult.success) {
                    this.executionQueue.stop();
                    stoppedOnError = true;
                }
                const completedTests = Object.keys(this.combinedTestResults).length;
                if (completedTests === testCount || (stoppedOnError && runningTests === 0)) {
                    resolve(this.combinedTestResults);
                }
            };

            const onTestIgnored = (wid: number, testResult: IgnoredTestResult) => {
                this.combinedTestResults[testResult.resultId] = testResult;
                testStatus.onTestIgnored(wid, testResult.resultId);
                runningTests--;
                const completedTests = Object.keys(this.combinedTestResults).length;
                if (completedTests === testCount || (stoppedOnError && runningTests === 0)) {
                    resolve(this.combinedTestResults);
                }
            };

            const onGridSlot = (resultId: string, gridInfo: SerializedGrid) => testStatus.onGridSlot(resultId, gridInfo);

            options.userData = {
                loginData: {
                    ...testimCustomToken.getTokenV3UserData(),
                    refreshToken: testimCustomToken.getRefreshToken()!,
                    authData: testimCustomToken.getTokenV3UserData(),
                    token,
                    userAccessKey,
                } as PartialLoginData,
                projectId: options.project,
                company: options.company!,
                servicesUrl: config.SERVICES_HOST(),
            };
            perf.log('in localStrategy before createWorker');
            // We create workers based on the parallel count.
            // Each worker is responsible for running a test, and when it's done if there is another test it will create a new Worker.
            while (this.workers.size < this.parallelCount && this.executionQueue.hasMoreTests()) {
                this.runWorker(
                    options,
                    this.customExtensionLocalLocation,
                    executionId,
                    onTestStarted,
                    onTestCompleted,
                    onGridSlot,
                    onTestIgnored,
                );
            }
        });

        try {
            const token = await testimCustomToken.getCustomTokenV3();
            const userAccessKey = await testimCustomToken.getUserAccessKey();
            const result = await runAndWaitToComplete(token, userAccessKey);
            // After all tests are done, there shouldn't be any workers running.
            if (this.workers.size > 0) {
                this.workers.clear();
            }
            if (stoppedOnError) {
                throw new StopRunOnError();
            }
            return result;
        } catch (err) {
            logger.error('failed running parallel workers', { executionId, err });
            throw err;
        }
    }

    private onWorkerFinished = async (workerId: number, ...args: BaseWorkerArgs) => {
        if (!this.workers.has(workerId)) {
            return;
        }
        this.workers.delete(workerId);
        if (this.executionQueue.hasMoreTests() && this.workers.size < this.parallelCount) {
            this.runWorker(...args);
        }
    };
    private generateUniqueIds(count: number): string[] {
        return Array.from({ length: count }, (_, index) => `worker-${index + 1}`);
    }

    private runWorker = async (...args: BaseWorkerArgs) => {
        const id = ++this.workerIdCount;
        // We set the worker to null to create the exact numbers of workers in the while loop.
        // As we don't want each worker to wait for the other to be created.
        this.workers.set(id, null);
        const testRunHandler = this.executionQueue.getNext();
        if (!testRunHandler) {
            this.workers.delete(id);
            return;
        }
        const uniqueId = this.workerUniqueIds[id % this.parallelCount];
        const Worker = await this.createWorker(testRunHandler.runConfig.browserValue, ...args);
        this.workers.set(id, Worker);
        Worker.id = id;
        Worker.uniqueId = uniqueId;
        Worker.run(testRunHandler, this.onWorkerFinished, ...args);
    };
}

